import { keys, pick } from 'lodash';
import { getCookie, isBrowser, setCookie } from '@zapier/common-utils';
import { extractParams } from '@zapier/url-utils';

import TrackingConstants, { ClickIds } from './constants';

const TrackingCookies = {
  /* Returns the cookie name based on the ID, e.g. __fbclid */
  getClickIdCookieName(id: ClickIds) {
    return `__${id}`;
  },

  /* Retrieves a cookie for a single click ID query parameter, e.g. fbclid, gclid, msclkid */
  retrieveClickId(id: ClickIds) {
    const cookie = getCookie(this.getClickIdCookieName(id));
    if (cookie) {
      try {
        return atob(decodeURIComponent(cookie));
      } catch (e) {
        console.error(`Error retrieving ${id}: ${e}`);
      }
    }
    return null;
  },

  /* Stores a cookie for a single click ID query parameter, e.g. fbclid, gclid, msclkid */
  storeClickId(id: ClickIds, href: string = window.location.href) {
    if (isBrowser()) {
      const params = extractParams(href);
      const param = params[id];
      if (param) {
        setCookie(
          this.getClickIdCookieName(id),
          encodeURIComponent(btoa(param)),
          TrackingConstants.CLICK_ID_COOKIE_EXPIRATION
        );
      }
    }
  },

  /* Iterates through all ClickIds values and fetches cookies for each, e.g. fbclid, gclid, msclkid */
  retrieveAllClickIds() {
    Object.values(ClickIds).forEach((id: ClickIds) => {
      this.retrieveClickId(id);
    });
  },

  /* Iterates through all ClickIds values and stores cookies for each, e.g. fbclid, gclid, msclkid */
  storeAllClickIds() {
    Object.values(ClickIds).forEach((id: ClickIds) => {
      this.storeClickId(id);
    });
  },

  retrieveUtms() {
    if (isBrowser()) {
      const utmCookie = getCookie(TrackingConstants.UTM_COOKIE_NAME);

      if (utmCookie) {
        try {
          return JSON.parse(utmCookie);
        } catch (e) {
          // If we fail to parse the cookie, then we will resolve everything
          // as `undefined`.
        }
      }
    }

    return {
      utm_campaign: undefined,
      utm_source: undefined,
      utm_content: undefined,
      utm_medium: undefined,
    };
  },

  retrieveUserIdentity() {
    const userIdentityCookie = getCookie(
      TrackingConstants.USER_IDENTITY_COOKIE_NAME
    );

    if (userIdentityCookie) {
      try {
        const rawCookie = atob(decodeURIComponent(userIdentityCookie));
        const parsedCookie = JSON.parse(rawCookie);
        return {
          email: parsedCookie.email,
          signupType: parsedCookie.signup_type,
        };
      } catch (e) {
        // If we fail to parse the cookie, then we will resolve everything
        // as `null`.
      }
    }

    return {
      email: null,
      signupType: null,
    };
  },

  clearUserIdentity() {
    setCookie(TrackingConstants.USER_IDENTITY_COOKIE_NAME, '', 1);
  },

  storeUtms(url?: string) {
    if (isBrowser()) {
      try {
        const href = url || window.location.href;
        const params = extractParams(href);
        const utmParams = pick(params, TrackingConstants.UTM_PARAM_NAMES);

        if (keys(utmParams).length > 0) {
          setCookie(
            TrackingConstants.UTM_COOKIE_NAME,
            JSON.stringify(utmParams),
            TrackingConstants.UTM_COOKIE_EXPIRATION
          );
        }

        return undefined;
      } catch (e) {
        // If we fail to set the cookie, then we will resolve `undefined`.
      }
    }

    return undefined;
  },
};
export default TrackingCookies;
